import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate } from "react-router";
import { Info, Layers, Lock } from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@app/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@app/components/ui/radio-group";
import { Label } from "@app/components/ui/label";
import DatasetStateBadge from "@app/components/DatasetStateBadge";
import { ExamplesTable } from "@app/components/ExamplesTable";
import { ExperimentStatusBadge } from "@app/components/ExperimentStatusBadge";
import API from "@app/services/api";
import { useIsAdmin } from "@app/utils/use-is-admin";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import {
  CANDIDATE_META_KEY,
  asInterval,
  fmtGapPoints,
  fmtHalfWidth,
  fmtScore,
  readRefusal,
  type DatasetState,
  type PassageCandidate,
  type ReferenceGap,
} from "@app/utils/reference-dataset";

export interface SealedReference {
  id: string;
  name: string;
  description: string | null;
  state: DatasetState;
  sealed_at: string | null;
}

interface ListedAssociation {
  patient_tenant_id?: string;
  patient_id_patient?: number;
  patient_nom?: string | null;
  patient_prenom?: string | null;
  patient_id_magex_versions?: string[];
}

type ListedExample = ComponentProps<typeof ExamplesTable>["examples"][number];

/** Example id → the patient's name, resolved like the work dataset's page: by any id the dossier has
 *  worn, then by (tenant, number). */
function namesByExample(
  associations: ListedAssociation[],
  examples: ListedExample[],
): Map<string, { nom: string | null; prenom: string | null }> {
  type Name = { nom: string | null; prenom: string | null };
  const byMagex = new Map<string, Name>();
  const byNumber = new Map<string, Name>();
  for (const a of associations) {
    const name = { nom: a.patient_nom ?? null, prenom: a.patient_prenom ?? null };
    for (const version of a.patient_id_magex_versions ?? []) byMagex.set(version, name);
    byNumber.set(`${a.patient_tenant_id}\t${a.patient_id_patient}`, name);
  }
  const names = new Map<string, Name>();
  for (const example of examples) {
    const idMagex = example.inputs?.patient_id_magex as string | undefined;
    const name =
      (idMagex ? byMagex.get(idMagex) : undefined) ??
      byNumber.get(`${example.inputs?.tenant_id}\t${example.inputs?.id_patient}`);
    if (name) names.set(example.id, name);
  }
  return names;
}

const EXAMPLES_PAGE_SIZE = 200;

interface Passage {
  id: string;
  name: string;
  status: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

function formatDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function candidateOf(passage: Passage): PassageCandidate | null {
  const value = passage.metadata?.[CANDIDATE_META_KEY];
  return value && typeof value === "object" ? (value as PassageCandidate) : null;
}

function gapOf(passage: Passage): ReferenceGap | null {
  const value = passage.metadata?.reference_gap;
  return value && typeof value === "object" ? (value as ReferenceGap) : null;
}

function scoreOf(passage: Passage): number | null {
  const value = passage.metadata?.macro_f1;
  return typeof value === "number" ? value : null;
}

/** « dans / au-delà de l'incertitude », said in weight, never in a hue: it is a machine state. */
function GapVerdict({ gap }: { gap: ReferenceGap | null }) {
  if (!gap) return <span className="text-slate-500">—</span>;
  return (
    <span className="whitespace-nowrap">
      <span
        className={
          gap.beyond_uncertainty
            ? "font-bold text-slate-900"
            : "font-medium text-slate-600"
        }
      >
        {fmtGapPoints(gap.gap)}
      </span>{" "}
      {gap.beyond_uncertainty === true && (
        <span className="font-semibold text-slate-900">
          <Trans>au-delà de l'incertitude</Trans>
        </span>
      )}
      {gap.beyond_uncertainty === false && (
        <span className="text-slate-500">
          <Trans>dans l'incertitude</Trans>
        </span>
      )}
      {gap.beyond_uncertainty === null && (
        <span className="text-slate-500">
          <Trans>incertitude inconnue</Trans>
        </span>
      )}
    </span>
  );
}

/**
 * The page of a sealed reference (PT-155): its scores, its registry of passages, and who it holds —
 * each dossier and the day of its act, read-only. Nothing a reader could tune the reading on: no
 * expected value, no run, no correction, no score by field.
 */
export default function SealedReferenceView({
  dataset,
  onChanged,
}: {
  dataset: SealedReference;
  onChanged: () => void;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const labReadOnly = useLabReadOnly();
  const [passages, setPassages] = useState<Passage[]>([]);
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [candidates, setCandidates] = useState<PassageCandidate[] | null>(null);
  const [chosen, setChosen] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const [unsealOpen, setUnsealOpen] = useState(false);
  const [examples, setExamples] = useState<ListedExample[]>([]);
  const [examplesTotal, setExamplesTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [associations, setAssociations] = useState<ListedAssociation[] | null>(null);

  const loadPassages = useCallback(async () => {
    const res = await API.get({
      path: `/dataset/${dataset.id}/experiments`,
      query: { limit: 100 },
    });
    setPassages(res?.ok ? (res.data.experiments as Passage[]) : []);
  }, [dataset.id]);

  useEffect(() => {
    void loadPassages();
  }, [loadPassages]);

  // Who is held out, and the day of each act: the API lists them for a sealed reference, and
  // nothing else about them.
  const loadExamples = useCallback(
    async (offset: number) => {
      const res = await API.get({
        path: `/dataset/${dataset.id}/examples`,
        query: { limit: EXAMPLES_PAGE_SIZE, offset },
      });
      if (!res?.ok) return;
      const page = (res.data.examples ?? []) as ListedExample[];
      setExamples((loaded) => (offset === 0 ? page : [...loaded, ...page]));
      setExamplesTotal(res.data.pagination?.total ?? page.length);
    },
    [dataset.id],
  );

  useEffect(() => {
    void loadExamples(0);
    void API.get({ path: `/dataset/${dataset.id}/patients` }).then((res) =>
      setAssociations(res?.ok ? ((res.data.associations ?? []) as ListedAssociation[]) : []),
    );
  }, [dataset.id, loadExamples]);

  const loadMoreExamples = async () => {
    setLoadingMore(true);
    try {
      await loadExamples(examples.length);
    } finally {
      setLoadingMore(false);
    }
  };

  const patientNameByExampleId = useMemo(
    () => namesByExample(associations ?? [], examples),
    [associations, examples],
  );

  // Numbered in the order they were run: the registry reads as a sequence of tries.
  const numbered = useMemo(() => {
    const byDate = [...passages].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
    const rank = new Map(byDate.map((p, index) => [p.id, index + 1]));
    return [...byDate].reverse().map((p) => ({ passage: p, rank: rank.get(p.id) ?? 0 }));
  }, [passages]);
  const latest = numbered.find(({ passage }) => passage.status === "COMPLETED");

  const openCandidates = async () => {
    setCandidatesOpen(true);
    setCandidates(null);
    setChosen("");
    const res = await API.get({
      path: `/dataset/${dataset.id}/passage-candidates`,
    });
    setCandidates(res?.ok ? (res.data.candidates as PassageCandidate[]) : []);
  };

  const createPassage = async () => {
    if (!chosen) return;
    setCreating(true);
    try {
      const res = await API.post({
        path: `/dataset/${dataset.id}/passages`,
        body: { candidate_experiment_id: chosen },
      });
      if (res?.ok && res.data?.id) {
        navigate(`/datasets/${dataset.id}/experiments/${res.data.id}`);
        return;
      }
      toast.error(readRefusal(res)?.message ?? t`Le passage n'a pas pu être créé.`);
    } finally {
      setCreating(false);
    }
  };

  const unseal = async () => {
    const res = await API.post({ path: `/dataset/${dataset.id}/unseal` });
    if (res?.ok) {
      toast.success(t`Le jeu est rendu au travail.`);
      onChanged();
      return;
    }
    toast.error(readRefusal(res)?.message ?? t`Le jeu n'a pas pu être descellé.`);
  };

  const latestCandidate = latest ? candidateOf(latest.passage) : null;
  const latestGap = latest ? gapOf(latest.passage) : null;
  const latestInterval = latest
    ? asInterval(latest.passage.metadata?.macro_f1_ci)
    : null;

  return (
    <div className="p-6">
      <div className="mb-6 space-y-2">
        <Link to="/datasets" className="text-sm text-primary hover:underline">
          <Trans>← Retour aux jeux de données</Trans>
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold">{dataset.name}</h1>
            <DatasetStateBadge state={dataset.state} />
          </div>
          {!labReadOnly && (
            <Button onClick={() => void openCandidates()}>
              <Lock className="mr-1 h-4 w-4" />
              <Trans>Évaluer un candidat</Trans>
            </Button>
          )}
        </div>
        {dataset.description && (
          <p className="whitespace-pre-line text-slate-600">{dataset.description}</p>
        )}
        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
          <span>
            <Trans>{passages.length} passages</Trans>
          </span>
          <span className="font-medium text-slate-700">
            <Trans>Vérité figée le {formatDay(dataset.sealed_at)}</Trans>
          </span>
        </div>
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div className="space-y-1 text-sm">
          <p className="font-semibold text-blue-900">
            <Trans>Ce jeu sert de référence : seuls ses scores agrégés sont affichés.</Trans>
          </p>
          <p className="text-slate-700">
            <Trans>
              Ses dossiers et la date de leurs actes sont listés, en lecture seule.
              Leurs valeurs attendues, ses runs et les corrections de ses dossiers ne
              sont consultables ni ici, ni par l'API. On n'y évalue qu'une expérience
              de travail terminée, et chaque passage reste au registre.
            </Trans>
          </p>
        </div>
      </div>

      {latest && (
        <section className="mb-6 space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">
            <Trans>Dernier passage · {formatDay(latest.passage.created_at)}</Trans>
          </h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">
                <Trans>Macro F1 · référence</Trans>
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {fmtScore(scoreOf(latest.passage))}{" "}
                <span className="text-base font-medium text-slate-500">
                  {fmtHalfWidth(latestInterval)}
                </span>
              </p>
              <p className="text-xs text-slate-500">
                <Trans>IC 95 %, par dossier</Trans>
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">
                <Trans>Même candidat · travail</Trans>
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {fmtScore(latestCandidate?.macro_f1)}{" "}
                <span className="text-base font-medium text-slate-500">
                  {fmtHalfWidth(asInterval(latestCandidate?.macro_f1_ci))}
                </span>
              </p>
              <p className="truncate text-xs text-slate-500">
                {latestCandidate?.dataset_name ?? "—"}
              </p>
            </div>
            <div className="rounded-lg border-2 border-slate-900 bg-white p-4">
              <p className="text-xs font-semibold text-slate-700">
                <Trans>Écart référence − travail</Trans>
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {latestGap ? fmtGapPoints(latestGap.gap) : "—"}
              </p>
              <p className="text-xs">
                <GapVerdict gap={latestGap} />
              </p>
            </div>
          </div>
        </section>
      )}

      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle>
            <Trans>Registre des passages ({passages.length})</Trans>
          </CardTitle>
          <p className="text-sm text-slate-500">
            <Trans>Chaque passage est inscrit ici et ne peut pas être retiré.</Trans>
          </p>
        </CardHeader>
        <CardContent>
          {numbered.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              <Trans>
                Aucun passage. Évaluez une expérience de travail terminée pour mesurer
                la lecture sur ce jeu.
              </Trans>
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-3 font-semibold"><Trans>N°</Trans></th>
                    <th className="py-2 pr-3 font-semibold"><Trans>Date</Trans></th>
                    <th className="py-2 pr-3 font-semibold"><Trans>Candidat</Trans></th>
                    <th className="py-2 pr-3 font-semibold"><Trans>Statut</Trans></th>
                    <th className="py-2 pr-3 text-right font-semibold"><Trans>Référence</Trans></th>
                    <th className="py-2 pr-3 text-right font-semibold"><Trans>Travail</Trans></th>
                    <th className="py-2 font-semibold"><Trans>Écart</Trans></th>
                  </tr>
                </thead>
                <tbody>
                  {numbered.map(({ passage, rank }) => {
                    const candidate = candidateOf(passage);
                    return (
                      <tr key={passage.id} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 pr-3 text-slate-500">{rank}</td>
                        <td className="py-3 pr-3">{formatDay(passage.created_at)}</td>
                        <td className="py-3 pr-3">
                          <Link
                            to={`/datasets/${dataset.id}/experiments/${passage.id}`}
                            className="font-semibold text-primary hover:underline"
                          >
                            {candidate?.experiment_name ?? passage.name}
                          </Link>
                          {candidate?.dataset_name && (
                            <span className="block text-xs text-slate-500">
                              <Trans>depuis {candidate.dataset_name}</Trans>
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-3">
                          <ExperimentStatusBadge status={passage.status} />
                        </td>
                        <td className="py-3 pr-3 text-right font-semibold">
                          {fmtScore(scoreOf(passage))}{" "}
                          <span className="font-normal text-slate-500">
                            {fmtHalfWidth(asInterval(passage.metadata?.macro_f1_ci))}
                          </span>
                        </td>
                        <td className="py-3 pr-3 text-right text-slate-600">
                          {fmtScore(candidate?.macro_f1)}
                        </td>
                        <td className="py-3">
                          <GapVerdict gap={gapOf(passage)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            <CardTitle>
              <Trans>Exemples ({examplesTotal})</Trans>
            </CardTitle>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            <Trans>
              Les dossiers et les actes que ce jeu note, en lecture seule : ni leurs
              valeurs attendues ni leur revue ne sont affichées.
            </Trans>
          </p>
        </CardHeader>
        <CardContent>
          <ExamplesTable
            examples={examples}
            total={examplesTotal}
            onLoadMore={() => void loadMoreExamples()}
            loadingMore={loadingMore}
            patientNameByExampleId={patientNameByExampleId}
            patientLinksLoading={associations === null}
            sealed
          />
        </CardContent>
      </Card>

      {isAdmin && !labReadOnly && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5">
          <div className="max-w-3xl space-y-1">
            <p className="font-semibold text-slate-900">
              <Trans>Desceller ce jeu</Trans>
            </p>
            <p className="text-sm text-slate-600">
              <Trans>
                Il redevient un jeu de travail, consultable en entier. Ses passages
                restent au registre. C'est définitif : un jeu qu'on a lu ne se rescelle
                pas.
              </Trans>
            </p>
          </div>
          <Button
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
            onClick={() => setUnsealOpen(true)}
          >
            <Trans>Desceller…</Trans>
          </Button>
        </div>
      )}

      <Dialog open={candidatesOpen} onOpenChange={setCandidatesOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              <Trans>Évaluer un candidat sur la référence</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                La configuration figée d'une expérience de travail terminée est
                évaluée sur ce jeu. Ce sera le passage n° {passages.length + 1} du
                registre : il ne pourra pas être retiré, et seul son score agrégé
                sera affiché.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          {candidates === null ? (
            <p className="py-4 text-sm text-slate-500">
              <Trans>Chargement...</Trans>
            </p>
          ) : candidates.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">
              <Trans>
                Aucune expérience de travail terminée dans cette spécialité.
              </Trans>
            </p>
          ) : (
            <RadioGroup
              value={chosen}
              onValueChange={setChosen}
              className="max-h-80 space-y-2 overflow-y-auto"
            >
              {candidates.map((candidate) => (
                <Label
                  key={candidate.experiment_id}
                  htmlFor={`candidate-${candidate.experiment_id}`}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 font-normal hover:border-primary/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                >
                  <RadioGroupItem
                    id={`candidate-${candidate.experiment_id}`}
                    value={candidate.experiment_id}
                    className="mt-0.5"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-slate-900">
                      {candidate.experiment_name}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {candidate.dataset_name} · {formatDay(candidate.completed_at)}
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-slate-900">
                    {fmtScore(candidate.macro_f1)}
                  </span>
                </Label>
              ))}
            </RadioGroup>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCandidatesOpen(false)}>
              <Trans>Annuler</Trans>
            </Button>
            <Button disabled={!chosen || creating} onClick={() => void createPassage()}>
              {creating ? t`Création...` : t`Créer le passage`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={unsealOpen} onOpenChange={setUnsealOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Desceller « {dataset.name} » ?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Le jeu redevient un jeu de travail : ses valeurs attendues, ses runs et
                ses corrections redeviennent consultables, et ses scores ne valent plus
                comme référence. On ne pourra pas le resceller.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Annuler</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => void unseal()}
            >
              <Trans>Desceller</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
